import React, {useState, useCallback, useMemo, useEffect} from 'react';
import {useSelector} from 'react-redux';
import styled from 'styled-components';
import {
	clientSideEventNames,
	documentTypes,
	IndividualFitDocumentTypes,
	NavigationPanelViews,
	TemporaryDocumentTypes,
	navigationFilterSelectOptions
} from '../../../../../../../../util/uiconstants';
import {labels} from '../../../../../../../../util/utils';
import Utility from '../../../../../../../../util/utilityfunctions';
import {useEngagement} from '../../../../../../../../util/customHooks';
import {
	useDocumentTypeId,
	useIsIndependenceOrProfile,
	useIsTrackChangesView,
	useSelectedStEntityId
} from '../../../../../../../../util/Ares/customHooks';
import {getTaskDocumentByDocumentId} from '../../../../../../../../selectors/Ares/selectors';
import {NavigationPanelContext} from '../NavigationPanelContext';
import ActivitiesList from './Activities/ActivitiesList';
import MainActivitiesLink from '../../NavigationPanel/MainActivitiesLink';
import CollapseExpandIcon from './CollapseExpandIcon';
import FormDetailsPanel from './FormDetailsPanel';
import ReactiveReviewModal from '../../../../../../../Common/ReactiveReviewModal';
import MESTSwitcher from '../MESTSwitcher';
import AllValidationsTriggerer from './Activities/AllValidationsTrigerer';
import FormValidationsTriggerer from './FormValidationsTriggerer';
import {useLocation} from 'react-router';

export default function NavigationPanelV2({engagementId, taskId, documentId, headerId, sectionId, onEntitySelected}) {
	const isTrackChangesView = useIsTrackChangesView();

	const engagement = useEngagement();
	const [selectedStEntityId] = useSelectedStEntityId();
	const isIndependenceOrProfile = useIsIndependenceOrProfile(documentId);

	const currentDocument = useSelector((state) => getTaskDocumentByDocumentId(state, documentId));
	const documentTypeId = useDocumentTypeId(currentDocument?.documentId);

	// Default search filter values:
	const defaultValidationStatus = navigationFilterSelectOptions[0].value;

	const [isExpanded, setIsExpanded] = useState(false);
	const [enabledAnimations, setEnabledAnimations] = useState(false);
	const [panelViewType, setPanelViewType] = useState();
	const [searchFilters, setSearchFilters] = useState();

	const [triggerValidations, setTriggerValidations] = useState(true);

	// These are just to keep track of selected items in the menu. Not the document shown on the right-side content (Section + Bodies)
	const [selectedIds, setSelectedIds] = useState();

	const selectedDocId = selectedIds?.selectedDocumentId;

	const isIndividualDocumentType = useMemo(() => IndividualFitDocumentTypes.includes(documentTypeId), [documentTypeId]);
	const isTemporaryDocumentType = useMemo(() => TemporaryDocumentTypes.includes(documentTypeId), [documentTypeId]);
	const isIndividualEntityProfileDocumentType = documentTypeId === documentTypes.MULTIENTITYINDIVIDUALPROFILE;

	const isMestEngagement = useMemo(() => engagement?.isMultiEntityEngagement, [engagement]);

	const showMainActivities =
		isExpanded &&
		!isIndependenceOrProfile &&
		!isTrackChangesView &&
		!isIndividualDocumentType &&
		!isTemporaryDocumentType;

	const location = useLocation();

	useEffect(() => {
		const pathSegments = location.pathname.split('/');
		const section = pathSegments[pathSegments.indexOf('section') + 1];
		const documentId = pathSegments[pathSegments.indexOf('guidedworkflowv2') + 1];

		const queryParams = new URLSearchParams(location.search);
		const taskId = queryParams.get('taskId');

		if (section && taskId) {
			setSelectedIds({selectedActivityId: taskId, selectedDocumentId: documentId});
		}
	}, [location]);

	const changeExpandedState = useCallback(
		(expanded, activityId, docId, animation = true) => {
			const _isExpanded = expanded === undefined ? !isExpanded : expanded;

			// These two take their defaults from the url, if not received as  parameters
			const _activityId = activityId || taskId;
			const _documentId = docId || documentId;

			setIsExpanded(_isExpanded);
			setEnabledAnimations(animation);

			if (_isExpanded) {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.navigationPanelExpanded));
			} else {
				//Left nav must always reflect what we see on the right side when collapsed.
				//We only reset it if we navigated somewhere else.
				//This handles main activities. We set it back to activity view.
				if (!_activityId && !_documentId) {
					changePanelViewType(NavigationPanelViews.Activity);
				} else {
					//This handles the expand when we are within a form.
					changePanelViewType(NavigationPanelViews.Sections, _activityId, _documentId);
				}
			}
		},
		[isExpanded, currentDocument, selectedIds, changePanelViewType]
	);

	const changePanelViewType = useCallback(
		(viewType, activityId, documentId) => {
			if (viewType === NavigationPanelViews.Activity) {
				setSelectedIds(undefined);
			} else if (viewType === undefined || viewType === NavigationPanelViews.Sections) {
				setSelectedIds({selectedActivityId: activityId, selectedDocumentId: documentId});
			}

			if (viewType) {
				setPanelViewType(viewType);
			}
		},
		[panelViewType]
	);

	// Default view
	if (!panelViewType) {
		if (documentId) {
			changePanelViewType(NavigationPanelViews.Sections, taskId, documentId);
		} else {
			changePanelViewType(NavigationPanelViews.Activity);
		}
	}

	// Search Filters:
	const updateSearchFilters = (searchFilter) => {
		setSearchFilters((prevFilters) => {
			let newValue = {...prevFilters, ...searchFilter};

			return newValue;
		});
	};

	const contextValue = useMemo(() => {
		return {
			engagementId,
			taskId,
			documentId,
			headerId,
			sectionId,
			isIndependenceOrProfile,
			stEntityId: selectedStEntityId,
			isExpanded,
			changeExpandedState,
			panelViewType,
			changePanelViewType,
			selectedIds,
			setSelectedIds,
			searchFilters,
			updateSearchFilters
		};
	}, [
		engagementId,
		taskId,
		documentId,
		headerId,
		sectionId,
		isIndependenceOrProfile,
		selectedStEntityId,
		isExpanded,
		changeExpandedState,
		panelViewType,
		changePanelViewType,
		selectedIds,
		setSelectedIds,
		searchFilters,
		updateSearchFilters
	]);

	const navigationPanelId = useMemo(() => 'fit-navigation-panel', []);

	// The key is built this way to force the component to re-mount when any of the url parameters change.
	const navigationPanelKey = useMemo(() => `$${documentId}_${headerId}_${sectionId}`, [
		documentId,
		headerId,
		sectionId
	]);

	const handleOnEntitySelected = useCallback(
		(stEntityId) => {
			// Update the selected entity id in the store. This will trigger a taskOverview call
			onEntitySelected && onEntitySelected(stEntityId);
		},
		[onEntitySelected]
	);

	useEffect(() => {
		updateSearchFilters({validationStatus: defaultValidationStatus});
	}, []);

	// Reset search filters to their default value when navigating to a different document
	useEffect(() => {
		if (selectedDocId) {
			updateSearchFilters({validationStatus: defaultValidationStatus});
		}
	}, [selectedDocId]);

	return (
		<StyledNavigationPanelV2
			id={navigationPanelId}
			key={navigationPanelKey}
			className={`StyledNavigationPanelV2
				${Utility.getTheme() === 'dark' ? ' dark' : ''}
				${!isExpanded ? ' StyledNavigationPanelCollapsed' : ' StyledNavigationPanelExpanded'}
				${enabledAnimations ? ' animate' : ''}`}
		>
			<NavigationPanelContext.Provider value={contextValue}>
				<ReactiveReviewModal entityUId={selectedDocId} />
				{panelViewType === undefined || panelViewType === NavigationPanelViews.Activity ? (
					// Activities view (Level 1)
					<>
						<AllValidationsTriggerer
							engagementId={engagementId}
							triggerValidations={triggerValidations}
							setTriggerValidations={setTriggerValidations}
							isMestEngagement={isMestEngagement}
						/>
						<section className="navigation-panel-header header-title main-activities-link">
							{showMainActivities && (
								<MainActivitiesLink
									title={labels.backToMainActivities}
									queryStringParams={{stEntityId: selectedStEntityId}}
								/>
							)}
						</section>
						{/* MEST Switcher */}
						{isMestEngagement && !isIndividualEntityProfileDocumentType && (
							<section className="navigation-panel-mest-switcher">
								<MESTSwitcher handleOnEntitySelected={handleOnEntitySelected} />
							</section>
						)}
						<section className="navigation-panel-header header-title">
							{isExpanded && labels.relatedActivities}
							<CollapseExpandIcon className={`${isExpanded ? ' collapse-expand-icon-expanded' : ''}`} />
						</section>
						<section className="navigation-panel-menu customScrollbar">
							<ActivitiesList engagementId={engagementId} documentTypeId={documentTypeId} isExpanded={isExpanded} />
						</section>
					</>
				) : (
					// Form details view (Level 2)
					<React.Fragment>
						{/* <FormValidationsTriggerer /> */}
						<FormDetailsPanel
							engagementId={engagementId}
							documentTypeId={documentTypeId}
							isMestEngagement={isMestEngagement}
							handleOnEntitySelected={handleOnEntitySelected}
						/>
					</React.Fragment>
				)}
			</NavigationPanelContext.Provider>
		</StyledNavigationPanelV2>
	);
}

const StyledNavigationPanelV2 = styled.section`
	--panel-width-expanded: var(--px-330);
	--panel-width-collapsed: var(--px-70);
	// Animation definitions
	@keyframes expand {
		0% {
			width: var(--panel-width-collapsed);
			opacity: 0.5;
		}
		100% {
			width: var(--panel-width-expanded);
			opacity: 1;
		}
	}
	@keyframes collapse {
		0% {
			width: var(--panel-width-expanded);
			opacity: 0.5;
		}
		100% {
			width: var(--panel-width-collapsed);
			opacity: 1;
		}
	}
	display: flex;
	height: 100%;
	flex-direction: column;
	align-items: flex-start;
	z-index: 5;
	padding: 0;
	position: absolute;
	background-color: var(--neutrals-bg-gradient1);
	color: var(--neutrals-900);
	box-shadow: 0px 2px 4px 0px rgba(35, 35, 47, 0.06), 0px 8px 16px 0px rgba(35, 35, 47, 0.08);
	border-right: var(--px-1) solid var(--neutrals-100);
	&.RightLoaderVisible {
		z-index: 2 !important;
	}
	&.dark {
		box-shadow: var(--px-6) var(--px-6) var(--px-15) 0 rgba(22, 22, 22, 0.75);
	}
	&.StyledNavigationPanelCollapsed {
		min-width: var(--panel-width-collapsed);
		.navigation-panel-header {
			justify-content: center;
		}
		&.animate {
			animation: collapse 0.15s linear 0s 1 normal forwards;
		}
		.StyledFormDetailsPanel {
			.form-footer {
				width: calc(var(--panel-width-collapsed) - var(--px-1));
				.motif-icon svg {
					width: var(--px-24);
					height: var(--px-24);
					color: var(--neutrals-900);
				}
				.notesControls,
				.signOffControls {
					display: flex;
					justify-content: center;
					.motif-icon {
						display: inline-flex;
						cursor: pointer;
					}
				}
			}
		}
	}
	&.StyledNavigationPanelExpanded {
		width: var(--panel-width-expanded);
		&.animate {
			animation: expand 0.15s linear 0s 1 normal forwards;
		}
		.StyledFormDetailsPanel {
			.form-footer {
				padding: var(--px-2) 0;
				width: calc(var(--panel-width-expanded) - var(--px-1));
				.notesControls {
					.motif-icon svg {
						width: var(--px-24);
						height: var(--px-24);
						color: var(--neutrals-900);
					}
				}
			}
		}
	}
	.navigation-panel-header {
		display: flex;
		width: 100%;
		justify-content: space-between;
		align-items: center;
		padding: var(--px-10) var(--px-10);
		border-bottom: var(--px-1) solid var(--neutrals-300);
		height: var(--px-60);
		&.header-title {
			font-size: var(--px-14);
			font-weight: 700;
			line-height: var(--px-24);
			color: var(--neutrals-900);
		}
		&.main-activities-link {
			justify-content: center;
		}
		.collapse-expand-icon-expanded {
			position: relative;
			left: var(--px-30);
		}
	}
	.navigation-panel-mest-switcher {
		width: 100%;
	}
	.navigation-panel-search,
	.navigation-panel-filter {
		width: 100%;
		border-bottom: var(--px-1) solid var(--neutrals-300);
	}
	.navigation-panel-menu {
		width: 100%;
		padding: 0;
		height: 100%;
	}
`;
